/**
 * FlowCoordinator - Generic coordinator interface for flow canvas entities
 *
 * Sprint: sprint-4, Task: T001
 * Requirement: FR-P3-001 - Generic FlowCoordinator<TEntity, TService> interface
 *
 * Architecture:
 * - Generic interface supporting any entity type (StepNode, StrategyBlock)
 * - Type-safe service parameter (StepNodeScreenService, StrategyBlockService)
 * - Enables code reuse between V2 screen and block coordinators
 * - Maintains Minimal Reactivity Architecture pattern
 *
 * Pattern: Generic interface with type parameters
 * - TEntity extends FlowEntity (entity type: StepNode, StrategyBlock)
 * - TService (Firebase service type: StepNodeScreenService, StrategyBlockService)
 *
 * Usage:
 * ```typescript
 * // Concrete implementations
 * class V2ScreenFlowCoordinator implements FlowCoordinator<FlowStepNode, StepNodeScreenService> { }
 * class BlockFlowCoordinator implements FlowCoordinator<StrategyBlock, StrategyBlockService> { }
 * ```
 */

import type {
  ReactFlowCanvasHandle,
  PositionOnConfigurator,
  EntityMove,
  FlowEntity
} from '@modules/reactFlow/types/flow-canvas-types';
import type { EntityTransition } from '@modules/reactFlow/types/EntityTransition';
import type { FunnelVariable } from '@segmently/schema-models/v2';

// ============================================================================
// BASE TYPES
// ============================================================================

// FlowEntity is now imported from flow-canvas-types (single source of truth)

/**
 * LoadingCallbacks - Callbacks for Firebase synchronization loading state
 *
 * Provides UI feedback during async Firebase operations (create, update, delete)
 * Enables non-blocking loader UI that doesn't block canvas interaction
 *
 * Sprint: firebase-sync-loading
 * Pattern: Observer pattern for async operation lifecycle
 *
 * Usage:
 * ```typescript
 * coordinator.setLoadingCallbacks({
 *   onSyncStart: (operation) => setIsSyncing(true),
 *   onSyncEnd: () => setIsSyncing(false),
 *   onSyncError: (error, operation) => console.error(error)
 * });
 * ```
 */
export interface LoadingCallbacks {
  /**
   * Called when Firebase operation starts
   * @param operation - Human-readable operation description (e.g., "Creating screen", "Deleting block")
   */
  onSyncStart?: (operation: string) => void;

  /**
   * Called when Firebase operation completes successfully
   */
  onSyncEnd?: () => void;

  /**
   * Called when Firebase operation fails
   * @param error - Error that occurred
   * @param operation - Operation that failed
   */
  onSyncError?: (error: Error, operation: string) => void;
}

/**
 * CreateEntityResult - Result of entity creation operation
 *
 * Generic type enabling type-safe entity creation for different entity types
 */
export interface CreateEntityResult<TEntity extends FlowEntity> {
  /** Created entity with all properties */
  entity: TEntity;
}

/**
 * CreateTransitionInput - Input for creating transitions between entities
 *
 * Uses generic "Entity" terminology to support both screens and blocks
 */
export interface CreateTransitionInput {
  sourceEntityId: string;
  targetEntityId: string;
  sourceActionKey: string;
  label?: string;
}

/**
 * FlowVariablesHost - Optional coordinator capability for hosts whose flow
 * variables live INSIDE the coordinator's own document instead of
 * flowVariablesService (projects/{pid}/onboardings/{oid}/versions/{vid}).
 *
 * The canvas editor duck-types this capability: when the active coordinator
 * exposes all three methods, variable load/save/delete route through the
 * coordinator (e.g. article drafts persist into flowDocument.variables);
 * otherwise the editor keeps its flowVariablesService behavior unchanged.
 */
export interface FlowVariablesHost {
  getFlowVariables(): Promise<Record<string, FunnelVariable>>;
  saveFlowVariable(variable: FunnelVariable): Promise<void>;
  deleteFlowVariable(variableId: string): Promise<void>;
}

/** Duck-type guard for the FlowVariablesHost coordinator capability. */
export function isFlowVariablesHost(coordinator: unknown): coordinator is FlowVariablesHost {
  const candidate = coordinator as Partial<FlowVariablesHost> | null | undefined;
  return Boolean(
    candidate
    && typeof candidate.getFlowVariables === 'function'
    && typeof candidate.saveFlowVariable === 'function'
    && typeof candidate.deleteFlowVariable === 'function'
  );
}

/**
 * FlowAnimationDefaults - Version-level motion defaults a coordinator can expose
 * to the canvas editor (onboarding: version.screensGraph.defaultScreenAnimation /
 * defaultTransition). The screen editor preview plays theme-inherited entrances
 * from them ("Inherited from theme" hint + Replay).
 */
export interface FlowAnimationDefaults {
  defaultScreenAnimation: Record<string, unknown> | null;
  defaultTransition: Record<string, unknown> | null;
}

type AnimationDefaultsCapability = Pick<
  FlowCoordinator<FlowEntity, unknown>,
  'getAnimationDefaults' | 'reloadAnimationDefaults'
>;

/**
 * Read the OPTIONAL animation-defaults capability of any coordinator variant.
 * Coordinators without it (article drafts, strategy blocks) yield nulls — no
 * version-level motion — instead of a TypeError. Takes `unknown` like
 * isFlowVariablesHost: the canvas editor's coordinator state can hold any
 * hosted variant.
 */
export function readFlowAnimationDefaults(coordinator: unknown): FlowAnimationDefaults {
  const candidate = coordinator as AnimationDefaultsCapability | null | undefined;
  return typeof candidate?.getAnimationDefaults === 'function'
    ? candidate.getAnimationDefaults()
    : { defaultScreenAnimation: null, defaultTransition: null };
}

/** Re-read the animation defaults through the OPTIONAL capability; a no-op for coordinators without it. */
export async function reloadFlowAnimationDefaults(coordinator: unknown): Promise<void> {
  const candidate = coordinator as AnimationDefaultsCapability | null | undefined;
  if (typeof candidate?.reloadAnimationDefaults === 'function') {
    await candidate.reloadAnimationDefaults();
  }
}

// ============================================================================
// GENERIC COORDINATOR INTERFACE
// ============================================================================

/**
 * FlowCoordinator - Generic coordinator interface for flow canvas lifecycle
 *
 * Manages entity lifecycle, transitions, canvas control, and Firebase persistence
 * Implements Minimal Reactivity Architecture pattern
 *
 * @template TEntity - Entity type extending FlowEntity (StepNode, StrategyBlock)
 * @template TService - Firebase service type (StepNodeScreenService, StrategyBlockService)
 */
export interface FlowCoordinator<
  TEntity extends FlowEntity,
  TService
> {
  // ============================================================================
  // LIFECYCLE METHODS (FR-P3-001: lifecycle_methods)
  // ============================================================================

  /**
   * Initialize coordinator with Firebase and load existing entities
   *
   * @param projectId - Parent project ID
   * @param strategyId - Parent strategy ID
   * @param args - Additional initialization parameters:
   *   - For screens: blockId (string), launchScreenId? (optional string)
   *   - For blocks: launchBlockId? (optional string)
   * @returns Promise resolving when initialization complete
   */
  initialize(projectId: string, strategyId: string, ...args: any[]): Promise<void>;

  /**
   * Cleanup coordinator resources
   *
   * Unsubscribes from Firebase, clears canvas handle, resets state
   * Idempotent - safe to call multiple times
   */
  destroy(): void;

  /**
   * Check if coordinator is initialized
   *
   * @returns true if initialized and ready for operations
   */
  isInitialized(): boolean;

  // ============================================================================
  // ACCESSOR METHODS (FR-P3-001: accessor_methods)
  // ============================================================================

  /**
   * Set loading callbacks for Firebase synchronization state
   *
   * Enables UI to display non-blocking loader during async Firebase operations
   * Callbacks are invoked for all create/update/delete operations
   *
   * Sprint: firebase-sync-loading
   *
   * @param callbacks - Loading state callbacks (onSyncStart, onSyncEnd, onSyncError)
   */
  setLoadingCallbacks(callbacks: LoadingCallbacks): void;

  /**
   * Set ReactFlow canvas handle for imperative operations
   *
   * @param handle - Canvas handle or null to clear
   */
  setCanvasHandle(handle: ReactFlowCanvasHandle | null): void;

  /**
   * Get ReactFlow canvas handle for imperative operations
   *
   * @returns Canvas handle or null if not set
   */
  getCanvasHandle(): ReactFlowCanvasHandle | null;

  /**
   * Get all entities managed by coordinator
   *
   * @returns Array of all entities (computed from registry)
   */
  getAllEntities(): TEntity[];

  /**
   * Get entity by ID (supports both entityId and Firestore id)
   *
   * Dual ID support: Canvas uses entityId, Firebase uses Firestore id
   * This method resolves both ID types for command validation
   *
   * @param id - Entity ID (entityId or Firestore id)
   * @returns Entity or undefined if not found
   */
  getEntity(id: string): TEntity | undefined;

  /**
   * Get all transitions between entities
   *
   * Computed on-demand from entity storage (NO cache)
   * FR-P3-005: Transitions extracted from screen.struct or block.action.edges
   *
   * @returns Array of all transitions
   */
  getAllTransitions(): EntityTransition[];

  /**
   * Get current launch entity ID
   *
   * Returns the entity marked as launch/entry point:
   * - For blocks: Entity with isLaunchBlock === true
   * - For screens: Entity with entityId === parent block's firstScreenId
   *
   * @returns Promise resolving with launch entity ID or null if no launch entity
   */
  getLaunchEntityId(): Promise<string | null>;

  /**
   * Set launch entity (automatically unsets old launch)
   *
   * Handles:
   * - Unsetting previous launch entity
   * - Setting new launch entity
   * - Deleting ALL incoming edges to the new launch entity
   * - Backing up affected entities for undo support
   * - Updating Canvas visual indicators (isLaunchScreen flag)
   * - Firebase persistence
   *
   * For blocks: Updates isLaunchBlock field
   * For screens: Updates parent block's firstScreenId field
   *
   * Pattern: Similar to deleteEntities - returns deletion results for undo support
   *
   * @param entityId - Entity ID to set as launch
   * @returns Promise resolving with deleted transition IDs and affected entities backup
   */
  setLaunchEntity(entityId: string): Promise<{
    deletedTransitionIds: string[];
    affectedEntitiesBackup: Map<string, TEntity>;
  }>;

  // ============================================================================
  // ENTITY OPERATIONS (FR-P3-001: entity_operations)
  // ============================================================================

  /**
   * Create new entity from template or data
   *
   * Handles: Registry registration, Canvas addition, Firebase persistence
   *
   * @param data - Partial entity data (without id and position)
   * @param position - Position on canvas (required x, y coordinates)
   * @returns Promise resolving with created entity with all properties
   */
  createEntity(data: Partial<TEntity>, position: PositionOnConfigurator): Promise<CreateEntityResult<TEntity>>;

  /**
   * Update existing entity
   *
   * Handles: Registry update, Canvas update, Firebase persistence
   *
   * @param entityId - Entity ID to update
   * @param updates - Partial entity updates
   */
  updateEntity(entityId: string, updates: Partial<TEntity>): void;

  /**
   * Update entity and refresh all connections/transitions
   *
   * Handles:
   * - Registry update with full entity data
   * - Canvas node update with new connection handles
   * - Refreshing all transitions on canvas
   * - Firebase persistence
   *
   * Used by: EditEntityCommand, entity editor dialogs
   *
   * Pattern: Uses EntityAdapter for entity-agnostic operations
   *
   * @param entityId - Entity ID to update
   * @param entity - Full entity with updates
   * @returns Promise resolving when all updates complete
   */
  updateEntityAndRefreshConnections(entityId: string, entity: TEntity): Promise<void>;

  /**
   * Delete entities and cleanup transitions
   *
   * Handles: Registry deletion, Canvas removal, Firebase deletion, transition cleanup
   *
   * @param entityIds - Array of entity IDs to delete
   * @returns Promise resolving with deleted transition IDs
   */
  deleteEntities(entityIds: string[]): Promise<{
    deletedTransitionIds: string[];
    affectedScreensBackup?: Map<string, TEntity>;
  }>;

  /**
   * Restore deleted entities (for undo support)
   *
   * Handles: Registry restoration, Canvas addition, Firebase restoration
   *
   * @param entities - Entities to restore
   * @param affectedScreensBackup - Backup of screens affected by deletion (optional)
   */
  restoreEntities(entities: TEntity[], affectedScreensBackup?: Map<string, TEntity>): Promise<void>;

  /**
   * Update entity positions (batch operation)
   *
   * Handles: Registry update, Canvas update, Firebase persistence
   * Used by: MoveEntityCommand, drag-and-drop operations
   *
   * @param moves - Array of entity position changes
   * @returns Promise resolving when all updates complete
   */
  updateEntityPositions(moves: EntityMove[]): Promise<void>;

  /**
   * Clone entity with new UUID and position offset
   *
   * ⚠️ IMPORTANT: All edges are CLEARED - no transitions are cloned
   *
   * Creates a deep copy of the source entity with:
   * - New UUID for id and entityId
   * - All edges/transitions removed (clean copy)
   * - Position offset from original
   * - Name appended with " (Copy)"
   *
   * For blocks: Also clones all child screens (without transitions)
   * For screens: Clones single screen (without transitions)
   *
   * Handles: Deep copy, edge clearing, Registry registration, Canvas addition, Firebase persistence
   *
   * @param sourceEntityId - Entity ID to clone
   * @param offset - Position offset { x, y } from original entity
   * @returns Promise resolving with cloned entity with new ID and empty edges
   */
  cloneEntity(sourceEntityId: string, offset: PositionOnConfigurator): Promise<CreateEntityResult<TEntity>>;

  // ============================================================================
  // TRANSITION OPERATIONS (FR-P3-001: transition_operations)
  // ============================================================================

  /**
   * Create transition between two entities
   *
   * Handles: Entity struct update, Registry update, Canvas edge addition, Firebase persistence
   *
   * @param input - Transition creation parameters
   * @returns Promise resolving with created transition
   */
  createTransition(input: CreateTransitionInput): Promise<EntityTransition>;

  /**
   * Update existing transition
   *
   * Handles: Entity struct update, Registry update, Canvas edge update, Firebase persistence
   *
   * @param transitionId - Transition ID to update
   * @param updates - Partial transition updates (label, etc.)
   * @returns Promise resolving with updated transition
   */
  updateTransition(transitionId: string, updates: Partial<Omit<EntityTransition, 'id'>>): Promise<EntityTransition>;

  /**
   * Delete transitions
   *
   * Handles: Entity struct update, Registry update, Canvas edge removal, Firebase persistence
   *
   * @param transitionIds - Array of transition IDs to delete
   * @returns Promise resolving when all deletions complete
   */
  deleteTransitions(transitionIds: string[]): Promise<void>;

  // ============================================================================
  // OPTIONAL CAPABILITIES (not every coordinator implements them)
  // ============================================================================

  /**
   * Version-level animation defaults captured with the last load. Only the
   * onboarding coordinator implements it (version.screensGraph); article-draft
   * and strategy-block coordinators have no version-level motion. Read it
   * through readFlowAnimationDefaults(), never directly.
   */
  getAnimationDefaults?(): FlowAnimationDefaults;

  /**
   * Re-read the animation defaults from storage after Apply→Animations rewrote
   * them. Same implementers as getAnimationDefaults; call it through
   * reloadFlowAnimationDefaults().
   */
  reloadAnimationDefaults?(): Promise<void>;
}
